import { CreateTaskSubTicketInput } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/inputs';
import { TaskSubTicketRef } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/refs';

import {
    RefReconcileOutcome,
    SubTicketRecoveryScope,
    ViewRefreshOutcome,
    reportSubTicketMutation,
} from '../../store/reconcile';

export interface SubmitCreateDeps {
    createSubTicket: (input: CreateTaskSubTicketInput) => Promise<TaskSubTicketRef>;

    // Surfaces the auto-added participant and the past-deadline confirmation, and repairs the
    // parent Task store when the participant list actually changed
    reconcileRef: (ref: TaskSubTicketRef, taskId: ID) => Promise<RefReconcileOutcome>;
}

export interface SubmitCreateParams {
    taskId: ID;
    input: CreateTaskSubTicketInput;
    scope: SubTicketRecoveryScope;
    deps: SubmitCreateDeps;
}

export interface SubmitCreateResult {
    ref: TaskSubTicketRef;
    reconciled: RefReconcileOutcome;
    views: ViewRefreshOutcome;
}

// Everything that happens after the server accepts the create, extracted from the component so
// the committed-but-stale reporting is exercisable without mounting a modal.
//
// A create that is refused throws straight out to the form's own error handling; nothing below
// runs unless the write committed. From that point the only remaining question is whether the
// two surfaces the create changed — the sub-ticket views and the parent Request's Participants
// card — were actually re-read, which is exactly what `reportSubTicketMutation` decides.
export const submitSubTicketCreate = async ({
    taskId,
    input,
    scope,
    deps,
}: SubmitCreateParams): Promise<SubmitCreateResult> => {
    const ref = await deps.createSubTicket(input);

    const reconciled = await deps.reconcileRef(ref, taskId);

    // The server assigns the ordinal and derives the summary, so the views are refetched rather
    // than reconstructed locally
    const views = await scope.refreshViews();

    reportSubTicketMutation({ successMessage: 'Sub-task created.', views, reconciled, scope });

    return { ref, reconciled, views };
};
